<template>
    <footer role="contentinfo" class="pro-footer">
        <div class="container">
            <nav class="links desktop" :aria-label="$t('a11y-labels.footer.nav')">
                <div class="column">
                    <pro-footer-section :section="sections.discover" />
                    <pro-footer-section :section="sections.showcase" />
                </div>

                <div class="column">
                    <pro-footer-section :section="sections.connect" />
                    <pro-footer-section :section="sections.marketplace" />
                </div>

                <div class="column">
                    <pro-footer-section :section="sections.discussions" />
                </div>

                <div class="column">
                    <pro-footer-section :section="sections.support" />
                    <pro-footer-section :section="sections.handbooks" />
                    <pro-footer-section :section="sections.account" />
                </div>
            </nav>
            <nav class="links mobile" :aria-label="$t('a11y-labels.footer.nav')">
                <div class="column">
                    <pro-footer-section :section="sections.discover" />
                    <pro-footer-section :section="sections.connect" />
                    <pro-footer-section :section="sections.handbooks" />
                    <pro-footer-section :section="sections.showcase" />
                </div>

                <div class="column">
                    <pro-footer-section :section="sections.discussions" />
                    <pro-footer-section :section="sections.marketplace" />
                    <pro-footer-section :section="sections.support" />
                    <pro-footer-section :section="sections.account" />
                </div>
            </nav>

            <nav class="legals" :aria-label="$t('a11y-labels.footer.legal')">
                <ul>
                    <li>
                        <pro-link :to="{ name: 'terms-conditions' }" :title="$t('footer.terms_conditions')">{{ $t("footer.terms_conditions") }}</pro-link>
                    </li>
                    <li>
                        <pro-link :to="{ name: 'privacy-policy' }" :title="$t('footer.privacy_policy')">{{ $t("footer.privacy_policy") }}</pro-link>
                    </li>
                    <li>
                        <pro-link :to="{ name: 'cookies-policy' }" :title="$t('footer.cookies_policy')">{{ $t("footer.cookies_policy") }}</pro-link>
                    </li>
                    <li>
                        <a href="http://savage.si" target="_blank" :title="$t('footer.careers')">{{ $t("footer.careers") }}</a>
                    </li>
                    <li>
                        <div class="locale-dropdown">
                            {{ currentLocale.name }}
                            <img :src="$asset(`images/flags/${currentLocale.iso}.png`)" alt="" />
                            <ProIcon icon="select-arrow" />
                            <label for="i18n-language-select" class="sr-only">{{ $t("global.language") }}</label>
                            <select id="i18n-language-select" :value="currentLocale.code" @change="updateLocale">
                                <option v-for="locale in availableLocales" :value="locale.code">{{ locale.name }}</option>
                            </select>
                        </div>
                    </li>
                </ul>
                <div class="copyright">
                    <span class="acknow-of-country">{{ $t("footer.aoc") }}</span> <br />
                    {{ $t("footer.copyright") }}
                </div>
            </nav>
        </div>
    </footer>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import ProFooterSection from "./ProFooterSection";

export default {
    components: { ProFooterSection },

    data: function () {
        return {};
    },
    computed: {
        ...mapGetters({
            isLoggedIn: "auth/isLoggedIn",
            user: "auth/getUser",
        }),
        availableLocales() {
            return this.$i18n.locales;
        },
        currentLocale() {
            return this.$i18n.locales.find((l) => l.code == this.$i18n.locale);
        },
        sections() {
            return {
                discover: [
                    {
                        title: this.$t("footer.discover"),
                        to: { name: "index" },
                    },
                    {
                        title: this.$t("footer.discover_whats_new"),
                        to: { name: "ipad-whats-new" },
                    },
                    {
                        title: this.$t("footer.discover_ipad"),
                        to: { name: "ipad" },
                    },
                    {
                        title: this.$t("footer.discover_pocket"),
                        to: { name: "pocket" },
                    },
                    {
                        title: this.$t("footer.discover_insight"),
                        to: { name: "insight" },
                    },
                    {
                        title: this.$t("footer.discover_art_prize"),
                        to: { name: "prize-2020" },
                    },
                    {
                        title: "Learn with Procreate",
                        to: "https://education.procreate.art",
                        external: true,
                    },
                ],

                showcase: [
                    {
                        title: this.$t("footer.showcase"),
                        to: "https://folio.procreate.art",
                        external: true,
                    },
                    {
                        title: this.$t("footer.showcase_featured"),
                        to: "https://folio.procreate.art",
                        external: true,
                    },
                    {
                        title: this.$t("footer.showcase_popular"),
                        to: "https://folio.procreate.art/showcase/popular",
                        external: true,
                    },
                    {
                        title: this.$t("footer.showcase_recent"),
                        to: "https://folio.procreate.art/showcase/recent",
                        external: true,
                    },
                ],

                connect: [
                    {
                        title: this.$t("footer.connect"),
                    },
                    {
                        title: this.$t("footer.connect_instagram"),
                        alt: this.$t("footer.connect_instagram_alt"),
                        to: "https://instagram.com/Procreate",
                        external: true,
                    },
                    {
                        title: this.$t("footer.connect_youtube"),
                        alt: this.$t("footer.connect_youtube_alt"),
                        to: "https://www.youtube.com/channel/UCU88WY0qX2bzDjjOLOj-Gng",
                        external: true,
                    },
                    {
                        title: this.$t("footer.connect_twitter"),
                        alt: this.$t("footer.connect_twitter_alt"),
                        to: "https://twitter.com/Procreate",
                        external: true,
                    },
                    {
                        title: this.$t("footer.connect_facebook"),
                        alt: this.$t("footer.connect_facebook_alt"),
                        to: "https://www.facebook.com/procreate",
                        external: true,
                    },
                    {
                        title: this.$t("footer.connect_weibo"),
                        alt: this.$t("footer.connect_weibo_alt"),
                        to: "https://www.weibo.com/procreateofficial",
                        external: true,
                    },
                    {
                        title: this.$t("footer.connect_red"),
                        alt: this.$t("footer.connect_red_alt"),
                        to: "https://www.xiaohongshu.com/user/profile/5d0712d4000000001000a6fc",
                        external: true,
                    },
                    {
                        title: this.$t("footer.connect_line"),
                        alt: this.$t("footer.connect_line_alt"),
                        to: "https://line.me/R/ti/p/@procreate?from=page",
                        external: true,
                    },
                    {
                        title: this.$t("footer.connect_twitter_jp"),
                        alt: this.$t("footer.connect_twitter_jp_alt"),
                        to: "https://twitter.com/Procreatejapan",
                        external: true,
                    },
                ],

                marketplace: [
                    {
                        title: this.$t("footer.marketplace"),
                        to: "https://folio.procreate.art/marketplace",
                        external: true,
                    },
                    {
                        title: this.$t("footer.marketplace_soon"),
                        alt: this.$t("footer.marketplace_soon_alt"),
                        to: "https://folio.procreate.art/marketplace",
                        external: true,
                    },
                ],

                discussions: [
                    {
                        title: this.$t("footer.discussions"),
                        to: "https://folio.procreate.art/discussions",
                        external: true,
                    },
                    {
                        title: this.$t("footer.discussions_ideas"),
                        alt: `${this.$t("footer.discussions")} - ${this.$t("footer.discussions_ideas")}`,
                        to: "https://folio.procreate.art/discussions/3",
                        external: true,
                    },
                    {
                        title: this.$t("footer.discussions_help"),
                        alt: `${this.$t("footer.discussions")} - ${this.$t("footer.discussions_help")}`,
                        to: "https://folio.procreate.art/discussions/4",
                        external: true,
                    },
                    {
                        title: this.$t("footer.discussions_marketplace"),
                        alt: `${this.$t("footer.discussions")} - ${this.$t("footer.discussions_marketplace")}`,
                        to: "https://folio.procreate.art/discussions/12",
                        external: true,
                    },
                    {
                        title: this.$t("footer.discussions_resources"),
                        alt: `${this.$t("footer.discussions")} - ${this.$t("footer.discussions_resources")}`,
                        to: "https://folio.procreate.art/discussions/10",
                        external: true,
                    },
                    {
                        title: this.$t("footer.discussions_general"),
                        alt: `${this.$t("footer.discussions")} - ${this.$t("footer.discussions_general")}`,
                        to: "https://folio.procreate.art/discussions/5",
                        external: true,
                    },
                    {
                        title: this.$t("footer.discussions_stories"),
                        alt: `${this.$t("footer.discussions")} - ${this.$t("footer.discussions_stories")}`,
                        to: "https://folio.procreate.art/discussions/8",
                        external: true,
                    },
                    {
                        title: this.$t("footer.discussions_advice"),
                        alt: `${this.$t("footer.discussions")} - ${this.$t("footer.discussions_advice")}`,
                        to: "https://folio.procreate.art/discussions/7",
                        external: true,
                    },
                    {
                        title: this.$t("footer.discussions_hardware"),
                        alt: `${this.$t("footer.discussions")} - ${this.$t("footer.discussions_hardware")}`,
                        to: "https://folio.procreate.art/discussions/11",
                        external: true,
                    },
                    {
                        title: this.$t("footer.discussions_website"),
                        alt: `${this.$t("footer.discussions")} - ${this.$t("footer.discussions_website")}`,
                        to: "https://folio.procreate.art/discussions/6",
                        external: true,
                    },
                ],

                support: [
                    {
                        title: this.$t("footer.support"),
                        to: { name: "support" },
                    },
                    {
                        title: this.$t("footer.support_faq"),
                        alt: this.$t("footer.support_faq_alt"),
                        to: { name: "faq" },
                    },
                ],

                handbooks: [
                    {
                        title: this.$t("footer.handbooks"),
                    },
                    {
                        title: this.$t("footer.handbooks_ipad"),
                        to: { name: "handbook-product-all", params: { product: "procreate", pathMatch: "introduction" } },
                    },
                    {
                        title: this.$t("footer.handbooks_pocket"),
                        to: { name: "handbook-product-all", params: { product: "pocket", pathMatch: "introduction" } },
                    },
                ],

                account: [
                    {
                        title: this.$t("footer.account"),
                    },
                    ...(() => {
                        if (this.isLoggedIn) {
                            return [
                                {
                                    title: this.$t("global.logout"),
                                    action: this.logout,
                                },
                                {
                                    title: this.$t("global.account_settings"),
                                    to: "https://folio.procreate.art/settings",
                                    external: true,
                                },
                            ];
                        }

                        return [
                            {
                                title: this.$t("global.login"),
                                action: this.login,
                            },
                            {
                                title: this.$t("footer.account_create"),
                                action: this.register,
                            },
                        ];
                    })(),
                ],
            };
        },
    },

    methods: {
        updateLocale(e) {
            this.$router.replace({ path: this.switchLocalePath(e.target.value) });
        },
        ...mapActions({
            login: "auth/openLoginModal",
            logout: "auth/logout",
            register: "auth/openRegisterModal",
        }),
    },
};
</script>
